import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Row, Space } from 'antd'
import {
  filter,
  includes,
  intersection,
  intersectionWith,
  isEmpty,
  isNil,
  noop,
  size,
  some,
} from 'lodash'
import { nanoid } from 'nanoid'
import PropTypes from 'prop-types'

import { ALLOWED_METADATA_TREE_OPERATIONS } from '../../constants'
import { nodePropType } from '../../prop-types'

const MetadataTreeEditableArea = ({
  metadataType,
  secondaryMetadataType,
  addNode,
  replaceNode,
  moveNodes,
  mainTreeData,
  supportingTreeData,
  isSupportingTreeShown,
  isReplaceActive,
  setIsReplaceActive,
  isMoveActive,
  setIsMoveActive,
  flatMainTreeData,
  flatSupportingTreeData,
  mainTreeCheckedKeys,
  setMainTreeCheckedKeys,
  supportingTreeCheckedKeys,
  setSupportingTreeCheckedKeys,
  onSupportingTreeExpand,
  shouldPickSelectedSupportingItem,
}) => {
  const allowedOperations = metadataType?.allowedOperations

  const isAddRootButtonShown = useMemo(
    () =>
      isSupportingTreeShown &&
      !isNil(addNode) &&
      includes(allowedOperations, ALLOWED_METADATA_TREE_OPERATIONS.ADD_ROOT),
    [addNode, allowedOperations, isSupportingTreeShown],
  )

  const isMoveButtonsShown = useMemo(
    () =>
      !isReplaceActive &&
      !isNil(moveNodes) &&
      !isEmpty(
        intersection(allowedOperations, [
          ALLOWED_METADATA_TREE_OPERATIONS.MOVE,
          ALLOWED_METADATA_TREE_OPERATIONS.MOVE_TO,
        ]),
      ),
    [allowedOperations, isReplaceActive, moveNodes],
  )

  const isReplaceButtonsShown = useMemo(
    () =>
      (!isMoveActive &&
        !isNil(replaceNode) &&
        includes(
          allowedOperations,
          ALLOWED_METADATA_TREE_OPERATIONS.REPLACE,
        )) ||
      includes(
        secondaryMetadataType?.allowedOperations,
        ALLOWED_METADATA_TREE_OPERATIONS.REPLACE,
      ),
    [
      allowedOperations,
      isMoveActive,
      replaceNode,
      secondaryMetadataType?.allowedOperations,
    ],
  )

  const isConfirmButtonEnabled = useMemo(
    () => !isEmpty(mainTreeCheckedKeys) && !isEmpty(supportingTreeCheckedKeys),
    [mainTreeCheckedKeys, supportingTreeCheckedKeys],
  )

  const [isFinishedCleaningUp, setIsFinishedCleaningUp] = useState(false)

  const handleCleanupOnToggleOffIsActive = useCallback(() => {
    setIsFinishedCleaningUp(true)
  }, [])

  const handleOnClickToggleMove = useCallback(() => {
    if (isMoveActive) {
      handleCleanupOnToggleOffIsActive()
    } else {
      setIsMoveActive(true)
    }
  }, [handleCleanupOnToggleOffIsActive, isMoveActive, setIsMoveActive])

  const handleOnClickToggleReplace = useCallback(() => {
    if (isReplaceActive) {
      handleCleanupOnToggleOffIsActive()
    } else {
      setIsReplaceActive(true)
    }
  }, [handleCleanupOnToggleOffIsActive, isReplaceActive, setIsReplaceActive])

  const handleConfirm = useCallback(
    (handler, setIsToggleActive) => {
      const mainSelectedNodes = intersectionWith(
        flatMainTreeData,
        mainTreeCheckedKeys,
        ({ key }, checkedKey) => key === checkedKey,
      )

      const supportingSelectedNodes = [
        ...filter(flatMainTreeData, treeItem =>
          some(
            intersectionWith(
              flatSupportingTreeData,
              supportingTreeCheckedKeys,
              ({ key }, checkedKey) => key === checkedKey,
            ),
            selectedSupportingItem =>
              shouldPickSelectedSupportingItem(
                treeItem,
                selectedSupportingItem,
              ),
          ),
        ),
      ]

      handler(mainSelectedNodes, supportingSelectedNodes)
      handleCleanupOnToggleOffIsActive(setIsToggleActive)
    },
    [
      flatMainTreeData,
      mainTreeCheckedKeys,
      supportingTreeCheckedKeys,
      handleCleanupOnToggleOffIsActive,
      flatSupportingTreeData,
      shouldPickSelectedSupportingItem,
    ],
  )

  const handleOnClickConfirmMove = useCallback(() => {
    const mainSelectedNodes = intersectionWith(
      flatMainTreeData,
      mainTreeCheckedKeys,
      ({ key }, checkedKey) => key === checkedKey,
    )

    const isMovedToRoot = includes(supportingTreeCheckedKeys, '0')

    const supportingSelectedNodes = [
      ...(isMovedToRoot ? supportingTreeData : []),

      ...filter(flatSupportingTreeData, treeItem =>
        some(
          intersectionWith(
            flatSupportingTreeData,
            supportingTreeCheckedKeys,
            ({ key }, checkedKey) => key === checkedKey,
          ),
          selectedSupportingItem =>
            shouldPickSelectedSupportingItem(treeItem, selectedSupportingItem),
        ),
      ),
    ]

    moveNodes(mainSelectedNodes, supportingSelectedNodes)
    handleCleanupOnToggleOffIsActive(setIsMoveActive)
  }, [
    flatMainTreeData,
    flatSupportingTreeData,
    handleCleanupOnToggleOffIsActive,
    mainTreeCheckedKeys,
    moveNodes,
    setIsMoveActive,
    shouldPickSelectedSupportingItem,
    supportingTreeCheckedKeys,
    supportingTreeData,
  ])

  const handleOnClickConfirmReplace = useCallback(
    () => handleConfirm(replaceNode, setIsReplaceActive),
    [handleConfirm, replaceNode, setIsReplaceActive],
  )

  const handleAddRootNode = useCallback(() => {
    const newNode = {
      id: nanoid(),
      name: '',
      key: `${size(mainTreeData)}`,
    }

    addNode({
      ...newNode,
      path: `[${newNode.key}]`,
      keyPath: [`${newNode.key}`],
      parentPath: '',
      isCurationEnabled: true,
      level: 1,
      idPath: [newNode.id],
      namePath: [newNode.name],
      allowedOperations,
    })
  }, [addNode, allowedOperations, mainTreeData])

  useEffect(() => {
    if (isFinishedCleaningUp) {
      onSupportingTreeExpand([])
      setMainTreeCheckedKeys([])
      setSupportingTreeCheckedKeys([])
      setIsReplaceActive(false)
      setIsMoveActive(false)
      setIsFinishedCleaningUp(false)
    }
  }, [
    isFinishedCleaningUp,
    mainTreeCheckedKeys,
    supportingTreeCheckedKeys,
    setIsMoveActive,
    setIsReplaceActive,
    setMainTreeCheckedKeys,
    setSupportingTreeCheckedKeys,
    onSupportingTreeExpand,
  ])

  return (
    <Row gutter={[0, 16]}>
      <Space>
        {!isAddRootButtonShown && (
          <Button onClick={handleAddRootNode}>Add Root</Button>
        )}

        {isMoveButtonsShown && (
          <>
            <Button onClick={handleOnClickToggleMove}>Toggle Move</Button>
            {isMoveActive && (
              <Button
                disabled={!isConfirmButtonEnabled}
                type="primary"
                onClick={handleOnClickConfirmMove}
              >
                Confirm Move
              </Button>
            )}
          </>
        )}

        {isReplaceButtonsShown && (
          <>
            <Button onClick={handleOnClickToggleReplace}>Toggle Replace</Button>
            {isReplaceActive && (
              <Button
                disabled={!isConfirmButtonEnabled}
                type="primary"
                onClick={handleOnClickConfirmReplace}
              >
                Confirm Replace
              </Button>
            )}
          </>
        )}
      </Space>
    </Row>
  )
}

MetadataTreeEditableArea.propTypes = {
  metadataType: PropTypes.object.isRequired,
  secondaryMetadataType: PropTypes.object,
  mainTreeData: PropTypes.arrayOf(nodePropType),
  supportingTreeData: PropTypes.arrayOf(nodePropType),
  addNode: PropTypes.func,
  moveNodes: PropTypes.func,
  replaceNode: PropTypes.func,
  isSupportingTreeShown: PropTypes.bool,
  isReplaceActive: PropTypes.bool,
  setIsReplaceActive: PropTypes.func,
  isMoveActive: PropTypes.bool,
  setIsMoveActive: PropTypes.func,
  flatMainTreeData: PropTypes.arrayOf(nodePropType),
  flatSupportingTreeData: PropTypes.arrayOf(nodePropType),
  mainTreeCheckedKeys: PropTypes.arrayOf(PropTypes.string),
  setMainTreeCheckedKeys: PropTypes.func,
  supportingTreeCheckedKeys: PropTypes.arrayOf(PropTypes.string),
  setSupportingTreeCheckedKeys: PropTypes.func,
  onSupportingTreeExpand: PropTypes.func,
  shouldPickSelectedSupportingItem: PropTypes.func,
}

MetadataTreeEditableArea.defaultProps = {
  secondaryMetadataType: undefined,
  mainTreeData: [],
  supportingTreeData: [],
  addNode: undefined,
  moveNodes: undefined,
  replaceNode: undefined,
  isSupportingTreeShown: false,
  isReplaceActive: false,
  setIsReplaceActive: undefined,
  isMoveActive: false,
  setIsMoveActive: undefined,
  flatMainTreeData: [],
  flatSupportingTreeData: [],
  mainTreeCheckedKeys: [],
  setMainTreeCheckedKeys: undefined,
  supportingTreeCheckedKeys: [],
  setSupportingTreeCheckedKeys: undefined,
  onSupportingTreeExpand: noop,
  shouldPickSelectedSupportingItem: (
    { id: mainNodeId },
    { id: supportingNodeId },
  ) => mainNodeId === supportingNodeId,
}

export default MetadataTreeEditableArea
